import ArgType, {withErrorsAllowed} from 'notebook/tables/excess/ArgType';
import FunctionCategory from 'notebook/tables/excess/FunctionCategory';
import {EvaluablePrimitiveSpreadsheetFunction} from 'notebook/tables/excess/FunctionConstants';
import {Range} from 'notebook/tables/excess/FunctionConstants';
import * as util from 'notebook/tables/excess/Util';
import {EvaluablePrimitive, coerceToPrimitive} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';

export default {
  name: 'IF',
  category: FunctionCategory.LOGIC,
  shortDescription: 'Returns value depending on logical expression.',
  description: 'Returns one value if a logical expression is TRUE and another if it is FALSE.',
  params: [
    {
      name: 'logical_test',
      // TODO(BNCH-114118): Make this ArgType.any and coerce to boolean
      type: ArgType.boolean,
      description:
        'An expression or reference to a cell containing an expression that represents some logical value, i.e. TRUE or FALSE.',
    },
    {
      name: 'value_if_true',
      // TODO (BNCH-114120): make this withErrorsAllowed(ArgType.singularValue)
      // Note: today, ranges are valid inputs to IF which customers are using. This outputs the coerceToPrimitive value of the array/range.
      // Ranges are NOT valid inputs to IF in Google Sheets/Excel and should instead produce #VALUE errors.
      // We have not made this change due to backwards incompatibility.
      type: withErrorsAllowed(ArgType.any),
      description: 'The value the function returns if logical_expression is TRUE.',
      // Do not coerce numeric-like arguments to numbers. Pass the document's language tag and snapshot
      // timezone so a datetime argument renders in that zone rather than the evaluating process's zone.
      coerce: (typedValue: TypedValue, context?) =>
        coerceToPrimitive(typedValue, {
          languageTag: context?.languageTag,
          timezone: context?.timezone,
        }),
    },
    {
      name: 'value_if_false',
      // TODO (BNCH-114120): make this withErrorsAllowed(ArgType.singularValue)
      type: withErrorsAllowed(ArgType.any),
      description: 'The value the function returns if logical_expression is FALSE.',
      // Do not coerce numeric-like arguments to numbers. Pass the document's language tag and snapshot
      // timezone so a datetime argument renders in that zone rather than the evaluating process's zone.
      coerce: (typedValue: TypedValue, context?) =>
        coerceToPrimitive(typedValue, {
          languageTag: context?.languageTag,
          timezone: context?.timezone,
        }),
    },
  ],
  eval: (logicalTest: boolean, valueIfTrue, valueIfFalse): EvaluablePrimitive => {
    const value = logicalTest ? valueIfTrue : valueIfFalse;
    if (value instanceof util.CellError) {
      throw value;
    }

    // TODO(BNCH-114120): This assertion is incorrect, since it CAN return a range.
    // Remove this assertion once we enforce that valueIfTrue and valueIfFalse are singular values.
    return value as Exclude<typeof value, Range<EvaluablePrimitive>>;
  },
} satisfies EvaluablePrimitiveSpreadsheetFunction;
